import { cmsSettingsAccess } from "../../../helpers/cmsSettingsAccess";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare, faCheckCircle, faCircleInfo, faLink, faPlugCircleCheck, faUnlink } from "@fortawesome/free-solid-svg-icons";
import { faFacebook, faGoogle, faInstagram, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import Loading from "../../components/common/Loading";
import AppEmptyState from "../../components/common/AppEmptyState";
import { SiteService } from "../../../services/SiteService";
import { ISiteDetails } from "../../../models/Interfaces";
import FacebookPublishingProvider from "../../../services/socialpublishing/FacebookPublishingProvider";
import TwitterPublishingProvider from "../../../services/socialpublishing/TwitterPublishingProvider";
import { EventBus } from "../../../helpers/EventBus";
import styles from "./socialMediaLinks.module.scss";

type PlatformCard = {
  key: string;
  name: string;
  icon: any;
  enabled: boolean;
  connected: boolean;
  canConnect: boolean;
  description: string;
  note: string;
};

const SocialMediaLinks = () => {
  const { isSiteOwner } = cmsSettingsAccess();
  const [loading, setLoading] = useState<boolean>(true);
  const [siteDetails, setSiteDetails] = useState<ISiteDetails | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [connecting, setConnecting] = useState<string | null>(null);

  const siteServiceRef = useRef<SiteService | null>(null);
  const facebookProviderRef = useRef<FacebookPublishingProvider>(new FacebookPublishingProvider());
  const twitterProviderRef = useRef<TwitterPublishingProvider>(new TwitterPublishingProvider());

  const getSiteDetails = async (): Promise<void> => {
    if (siteServiceRef.current) {
      const res = await siteServiceRef.current.getSiteDetails();
      setSiteDetails(res);
    }
  };

  const refreshConnections = (): void => {
    setRefreshKey((prev) => prev + 1);
  };

  const getPlatformCards = useMemo<Array<PlatformCard>>(() => {
    const fbConnected = facebookProviderRef.current.isConnected();
    const xConnected = twitterProviderRef.current.isConnected();

    return [
      {
        key: "facebook",
        name: "Facebook",
        icon: faFacebook,
        enabled: siteDetails?.ShareFacebook ?? false,
        connected: fbConnected,
        canConnect: true,
        description: "Connect a Facebook account so editors can publish shared page content directly to Facebook.",
        note: fbConnected ? "Facebook account connected for this browser session." : "No Facebook account connected in this browser yet."
      },
      {
        key: "x",
        name: "X",
        icon: faXTwitter,
        enabled: siteDetails?.ShareX ?? false,
        connected: xConnected,
        canConnect: true,
        description: "Connect an X account so editors can publish shared page content directly to X.",
        note: xConnected ? "X account tokens are available in this browser." : "No X account connected in this browser yet."
      },
      {
        key: "instagram",
        name: "Instagram",
        icon: faInstagram,
        enabled: siteDetails?.ShareInstagram ?? false,
        connected: false,
        canConnect: false,
        description: "Instagram is listed in site sharing options, but direct CMS account linking is not available in the current sharing flow.",
        note: "This platform is enabled at site level, but account linking is not yet available here."
      },
      {
        key: "google",
        name: "Google",
        icon: faGoogle,
        enabled: siteDetails?.ShareGoogle ?? false,
        connected: false,
        canConnect: false,
        description: "Google is listed in site sharing options, but direct CMS account linking is not available in the current sharing flow.",
        note: "This platform is enabled at site level, but account linking is not yet available here."
      }
    ];
  }, [siteDetails, refreshKey]);

  const connectFacebook = async (): Promise<void> => {
    try {
      setConnecting("facebook");
      await facebookProviderRef.current.connect();
      refreshConnections();
    } finally {
      setConnecting(null);
    }
  };

  const disconnectFacebook = async (): Promise<void> => {
    try {
      setConnecting("facebook");
      await facebookProviderRef.current.disconnect();
      refreshConnections();
    } finally {
      setConnecting(null);
    }
  };

  const connectX = async (): Promise<void> => {
    try {
      setConnecting("x");
      await twitterProviderRef.current.connect();
      refreshConnections();
    } finally {
      setConnecting(null);
    }
  };

  const disconnectX = (): void => {
    setConnecting("x");
    void twitterProviderRef.current.disconnect();
    refreshConnections();
    setConnecting(null);
  };

  useEffect(() => {
    const load = async () => {
      try {
        await getSiteDetails();
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    if (!siteServiceRef.current) {
      siteServiceRef.current = new SiteService();
    }

    load();
  }, []);

  useEffect(() => {
    const handleMessage = (evt: MessageEvent): void => {
      if (evt.origin !== window.location.origin) {
        return;
      }

      if (evt.data?.event === EventBus.Event.OnSocialMediaPublishingConnected) {
        refreshConnections();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <>
      {loading && (
        <Loading Message="Loading social media sharing setup..." SpinSize="5x" />
      )}

      {!loading && (
        <div className={styles.page}>
          <section className="app-list-section">
            <div className="app-list-section__header">
              <div className={styles.sectionLead}>
                <div>
                  <h2 className="app-list-section__title">Sharing Platform Setup</h2>
                </div>
                <div className={styles.headerActions}>
                  {isSiteOwner && <Link to="/account/sitedetails" className="btn btn-secondary">
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    Open Site Details
                  </Link>}
                </div>
              </div>
            </div>
            <div className="app-list-section__body">
              {getPlatformCards.some(card => card.enabled) ? (
                <div className={styles.cardGrid}>
                  {getPlatformCards.map((card) => (
                    <article key={card.key} className={styles.platformCard}>
                      <div className={styles.platformHead}>
                        <div className={styles.platformIdentity}>
                          <span className={styles.platformIconWrap}>
                            <FontAwesomeIcon icon={card.icon} className={styles.platformIcon} />
                          </span>
                          <div>
                            <h3 className={styles.platformTitle}>{card.name}</h3>
                            <p className={styles.platformDescription}>{card.description}</p>
                          </div>
                        </div>
                        <span className={card.enabled ? styles.statusBadgeOn : styles.statusBadgeOff}>
                          {card.enabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>

                      <div className={styles.platformMeta}>
                        <div className={styles.metaRow}>
                          <span className={styles.metaLabel}>Site setting</span>
                          <span className={styles.metaValue}>{card.enabled ? "Available to editors" : "Disabled in site settings"}</span>
                        </div>
                        <div className={styles.metaRow}>
                          <span className={styles.metaLabel}>Account status</span>
                          <span className={card.connected ? styles.connectedValue : styles.disconnectedValue}>
                            <FontAwesomeIcon icon={card.connected ? faCheckCircle : faCircleInfo} />
                            {card.connected ? "Connected" : (card.canConnect ? "Not connected" : "Not available")}
                          </span>
                        </div>
                        <p className={styles.platformNote}>{card.note}</p>
                      </div>

                      <div className={styles.platformActions}>
                        {!card.enabled && isSiteOwner && (
                          <Link to="/account/sitedetails" className="btn btn-secondary">
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                            Enable In Site Details
                          </Link>
                        )}

                        {card.enabled && card.key === "facebook" && (
                          card.connected ? (
                            <Button variant="secondary" onClick={disconnectFacebook} disabled={connecting === "facebook"}>
                              <FontAwesomeIcon icon={faUnlink} />
                              Disconnect Facebook
                            </Button>
                          ) : (
                            <Button variant="primary" onClick={connectFacebook} disabled={connecting === "facebook"}>
                              <FontAwesomeIcon icon={faPlugCircleCheck} />
                              Connect Facebook
                            </Button>
                          )
                        )}

                        {card.enabled && card.key === "x" && (
                          card.connected ? (
                            <Button variant="secondary" onClick={disconnectX} disabled={connecting === "x"}>
                              <FontAwesomeIcon icon={faUnlink} />
                              Disconnect X
                            </Button>
                          ) : (
                            <Button variant="primary" onClick={connectX} disabled={connecting === "x"}>
                              <FontAwesomeIcon icon={faPlugCircleCheck} />
                              Connect X
                            </Button>
                          )
                        )}

                        {card.enabled && !card.canConnect && isSiteOwner && (
                          <Link to="/account/sitedetails" className="btn btn-secondary">
                            <FontAwesomeIcon icon={faLink} />
                            Review Site Sharing Settings
                          </Link>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyWrap}>
                  <AppEmptyState
                    compact
                    title="No sharing platforms enabled"
                    message="Turn on the social platforms you want to use in Site Details first, then return here to review connection readiness for sharing."
                  />
                  <div className={styles.emptyActions}>
                    {isSiteOwner && <Link to="/account/sitedetails" className="btn btn-primary">
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                      Open Site Details
                    </Link>}
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default SocialMediaLinks;
